// ============================================================
// src/components/admin/BillingDashboard.js
// ✅ Real data from /api/billing/dashboard-stats
// ✅ Fully isolated per hospital via JWT hospitalId
// ✅ PranJeev color scheme — teal #0d7377 + navy #0a1628 + gold #c9a845
// ✅ Fully responsive — mobile, tablet, desktop
// ✅ Hover effects on all cards and rows
// ✅ Daily / Weekly / Monthly / Yearly toggle + Date picker
// ✅ Line chart — Revenue trend vs Target
// ✅ Donut chart — Payment status (Paid/Partial/Unpaid)
// ✅ Bar chart — Top treatments by revenue
// ✅ NEW: Payment Method donut (Cash/UPI/Card/Online)
// ✅ NEW: New vs Returning patients bar chart
// ✅ NEW: Summary stats row (Avg invoice, Max invoice, Total count)
// ✅ IST timezone fix for daily hourly chart
// ============================================================
import React, { useState, useEffect, useRef, useCallback } from "react";
import api from "../../utils/api";

const TEAL = "#0d7377";
const TEAL2 = "#14a0a5";
const GOLD = "#c9a845";
const NAVY = "#0a1628";
const RED = "#ef4444";
const BLUE = "#2563eb";
const GREEN = "#16a34a";
const ORANGE = "#f97316";
const PURPLE = "#7c3aed";

const METHOD_COLORS = {
  Cash: TEAL,
  UPI: GOLD,
  Card: BLUE,
  Online: PURPLE,
  Cheque: ORANGE,
  Other: "#94a3b8",
};

function fmt(n) {
  if (n === undefined || n === null) return "₹0";
  return "₹" + Number(n).toLocaleString("en-IN");
}

function loadChartJs() {
  return new Promise((resolve) => {
    if (window.Chart) return resolve(true);
    if (document.getElementById("chartjs-cdn")) {
      const check = setInterval(() => {
        if (window.Chart) {
          clearInterval(check);
          resolve(true);
        }
      }, 100);
      return;
    }
    const s = document.createElement("script");
    s.id = "chartjs-cdn";
    s.src =
      "https://cdnjs.cloudflare.com/ajax/libs/Chart.js/4.4.1/chart.umd.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.head.appendChild(s);
  });
}

const css = `
.bd-wrap { padding:0; font-family:"DM Sans",sans-serif; }
.bd-header { background:linear-gradient(135deg,#0a1628,#0d2444); padding:20px 24px; display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:12px; }
.bd-header-title { font-size:17px; font-weight:700; color:#fff; margin-bottom:3px; }
.bd-header-sub { font-size:12px; color:rgba(255,255,255,0.5); }
.bd-filters { display:flex; gap:8px; flex-wrap:wrap; align-items:center; }
.bd-seg { display:flex; background:rgba(255,255,255,0.1); border-radius:8px; padding:3px; gap:2px; }
.bd-seg-btn { font-size:12px; padding:5px 13px; border:none; border-radius:6px; cursor:pointer; background:transparent; color:rgba(255,255,255,0.6); font-family:"DM Sans",sans-serif; font-weight:500; transition:all 0.18s; }
.bd-seg-btn:hover { background:rgba(255,255,255,0.1); color:#fff; }
.bd-seg-btn.active { background:#0d7377; color:#fff; font-weight:700; }
.bd-date { font-size:12px; padding:6px 12px; border:1px solid rgba(255,255,255,0.2); border-radius:8px; background:rgba(255,255,255,0.08); color:#fff; font-family:"DM Sans",sans-serif; cursor:pointer; }
.bd-date::-webkit-calendar-picker-indicator { filter:invert(1); opacity:0.6; }
.bd-body { padding:20px 24px; background:#f8fafc; min-height:400px; }
.bd-cmp-row { display:grid; grid-template-columns:repeat(3,1fr); gap:12px; margin-bottom:16px; }
.bd-cmp { background:#fff; border-radius:12px; padding:14px 18px; border-left:3px solid #0d7377; box-shadow:0 2px 8px rgba(0,0,0,0.06); transition:transform 0.18s,box-shadow 0.18s; cursor:default; }
.bd-cmp:hover { transform:translateY(-2px); box-shadow:0 6px 20px rgba(0,0,0,0.1); }
.bd-cmp:nth-child(2) { border-left-color:#c9a845; }
.bd-cmp:nth-child(3) { border-left-color:#14a0a5; }
.bd-cmp-label { font-size:11px; color:#94a3b8; font-weight:500; text-transform:uppercase; letter-spacing:0.5px; margin-bottom:5px; }
.bd-cmp-val { font-size:22px; font-weight:700; color:#0a1628; line-height:1; margin-bottom:4px; }
.bd-cmp-delta { font-size:11px; font-weight:600; }
.bd-kpi-grid { display:grid; grid-template-columns:repeat(4,1fr); gap:12px; margin-bottom:16px; }
.bd-kpi { background:#fff; border-radius:12px; padding:16px 18px; box-shadow:0 2px 8px rgba(0,0,0,0.06); transition:transform 0.18s,box-shadow 0.18s; cursor:default; }
.bd-kpi:hover { transform:translateY(-3px); box-shadow:0 8px 24px rgba(0,0,0,0.1); }
.bd-kpi-icon { font-size:20px; margin-bottom:6px; }
.bd-kpi-val { font-size:24px; font-weight:700; color:#0a1628; line-height:1; margin-bottom:3px; }
.bd-kpi-label { font-size:11px; color:#94a3b8; margin-bottom:5px; }
.bd-kpi-change { font-size:11px; font-weight:600; }
.bd-charts-row { display:grid; grid-template-columns:1.5fr 1fr; gap:12px; margin-bottom:16px; }
.bd-charts-row-equal { display:grid; grid-template-columns:1fr 1fr; gap:12px; margin-bottom:16px; }
.bd-card { background:#fff; border-radius:12px; padding:18px 20px; box-shadow:0 2px 8px rgba(0,0,0,0.06); }
.bd-card-header { display:flex; justify-content:space-between; align-items:flex-start; margin-bottom:12px; }
.bd-card-title { font-size:13px; font-weight:700; color:#0a1628; }
.bd-card-sub { font-size:11px; color:#94a3b8; }
.bd-legend { display:flex; flex-wrap:wrap; gap:10px; margin-bottom:10px; font-size:11px; color:#64748b; }
.bd-legend span { display:flex; align-items:center; gap:4px; }
.bd-dot { width:9px; height:9px; border-radius:2px; display:inline-block; }
.bd-summary-row { display:grid; grid-template-columns:repeat(3,1fr); gap:12px; margin-bottom:16px; }
.bd-summary { background:linear-gradient(135deg,#0a1628,#0d2444); border-radius:12px; padding:16px 18px; transition:transform 0.18s,box-shadow 0.18s; cursor:default; }
.bd-summary:hover { transform:translateY(-2px); box-shadow:0 8px 24px rgba(10,22,40,0.25); }
.bd-summary-icon { font-size:18px; margin-bottom:6px; }
.bd-summary-val { font-size:22px; font-weight:700; color:#fff; line-height:1; margin-bottom:3px; }
.bd-summary-label { font-size:11px; color:rgba(255,255,255,0.5); }
.bd-inv-row { display:flex; justify-content:space-between; align-items:center; padding:10px 12px; border-radius:10px; margin-bottom:6px; background:#f8fafc; border:1px solid #f1f5f9; transition:background 0.15s,transform 0.15s,box-shadow 0.15s; cursor:default; }
.bd-inv-row:hover { background:#fff; transform:translateX(3px); box-shadow:0 2px 12px rgba(13,115,119,0.1); border-color:#e2e8f0; }
.bd-inv-name { font-size:13px; font-weight:600; color:#0a1628; margin-bottom:2px; }
.bd-inv-meta { font-size:11px; color:#94a3b8; }
.bd-badge { font-size:10px; padding:3px 10px; border-radius:20px; font-weight:700; letter-spacing:0.3px; }
.bd-badge-paid { background:#dcfce7; color:#16a34a; }
.bd-badge-unpaid { background:#fee2e2; color:#dc2626; }
.bd-badge-partial { background:#fef3c7; color:#d97706; }
.bd-empty { text-align:center; padding:40px; color:#94a3b8; font-size:14px; }
.bd-loading { text-align:center; padding:60px; color:#0d7377; font-size:14px; }
.bd-refresh-btn { font-size:11px; padding:5px 12px; background:linear-gradient(135deg,#0d7377,#14a0a5); color:#fff; border:none; border-radius:6px; cursor:pointer; font-family:"DM Sans",sans-serif; font-weight:600; transition:opacity 0.18s; }
.bd-refresh-btn:hover { opacity:0.85; }
.bd-method-list { margin-top:8px; }
.bd-method-row { display:flex; justify-content:space-between; align-items:center; padding:6px 0; border-bottom:1px solid #f1f5f9; font-size:12px; }
.bd-method-row:last-child { border-bottom:none; }
.bd-method-name { display:flex; align-items:center; gap:6px; color:#475569; font-weight:500; }
.bd-method-dot { width:8px; height:8px; border-radius:50%; }
.bd-method-amount { font-weight:700; color:#0a1628; }
@media(max-width:860px) {
  .bd-kpi-grid { grid-template-columns:repeat(2,1fr); }
  .bd-charts-row { grid-template-columns:1fr; }
  .bd-charts-row-equal { grid-template-columns:1fr; }
  .bd-cmp-row { grid-template-columns:1fr; }
  .bd-summary-row { grid-template-columns:repeat(3,1fr); }
  .bd-header { padding:16px 16px; }
  .bd-body { padding:16px 14px; }
}
@media(max-width:480px) {
  .bd-kpi-grid { grid-template-columns:repeat(2,1fr); gap:8px; }
  .bd-kpi-val { font-size:20px; }
  .bd-cmp-val { font-size:18px; }
  .bd-seg-btn { font-size:11px; padding:5px 9px; }
  .bd-summary-row { grid-template-columns:1fr; }
  .bd-body { padding:12px 10px; }
}
`;

export default function BillingDashboard({ showNotify }) {
  const [period, setPeriod] = useState("daily");
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const trendRef = useRef(null);
  const donutRef = useRef(null);
  const treatRef = useRef(null);
  const methodRef = useRef(null);
  const patientRef = useRef(null);

  const trendChart = useRef(null);
  const donutChart = useRef(null);
  const treatChart = useRef(null);
  const methodChart = useRef(null);
  const patientChart = useRef(null);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const d = await api.get(
        `/billing/dashboard-stats?period=${period}&date=${date}`,
      );
      setData(d);
    } catch (err) {
      showNotify && showNotify("Error", "Failed to load dashboard data", true);
    }
    setLoading(false);
  }, [period, date]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    if (!data) return;
    loadChartJs().then((ok) => {
      if (!ok || !window.Chart) return;
      const gc = "rgba(0,0,0,0.06)";
      const tc = "rgba(0,0,0,0.4)";

      // ── 1. Revenue trend line chart ───────────────────────
      if (trendChart.current) trendChart.current.destroy();
      if (trendRef.current) {
        trendChart.current = new window.Chart(trendRef.current, {
          type: "line",
          data: {
            labels: data.labels || [],
            datasets: [
              {
                label: "Revenue",
                data: data.revenueData || [],
                borderColor: TEAL,
                backgroundColor: "rgba(13,115,119,0.08)",
                borderWidth: 2.5,
                pointBackgroundColor: TEAL,
                pointRadius: 4,
                pointHoverRadius: 6,
                fill: true,
                tension: 0.38,
              },
              {
                label: "Target",
                data: (data.labels || []).map(() => data.target || 0),
                borderColor: GOLD,
                borderWidth: 1.5,
                borderDash: [5, 4],
                pointRadius: 0,
                fill: false,
              },
            ],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: { legend: { display: false } },
            scales: {
              x: {
                grid: { color: gc },
                ticks: {
                  color: tc,
                  font: { size: 10 },
                  maxRotation: 45,
                  autoSkip: true,
                  maxTicksLimit: 12,
                },
              },
              y: {
                grid: { color: gc },
                ticks: {
                  color: tc,
                  font: { size: 10 },
                  callback: (v) => "₹" + v.toLocaleString("en-IN"),
                },
                beginAtZero: true,
              },
            },
          },
        });
      }

      // ── 2. Payment status donut chart ─────────────────────
      if (donutChart.current) donutChart.current.destroy();
      if (donutRef.current) {
        const p = data.paidCount || 0,
          u = data.unpaidCount || 0,
          pa = data.partialCount || 0;
        donutChart.current = new window.Chart(donutRef.current, {
          type: "doughnut",
          data: {
            labels: ["Paid", "Partial", "Unpaid"],
            datasets: [
              {
                data: [p || 0.001, pa || 0.001, u || 0.001],
                backgroundColor: [TEAL, GOLD, RED],
                borderWidth: 0,
                hoverOffset: 8,
              },
            ],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            cutout: "68%",
            plugins: { legend: { display: false } },
          },
        });
      }

      // ── 3. Top treatments horizontal bar chart ────────────
      if (treatChart.current) treatChart.current.destroy();
      if (
        treatRef.current &&
        data.topTreatments &&
        data.topTreatments.length > 0
      ) {
        treatChart.current = new window.Chart(treatRef.current, {
          type: "bar",
          data: {
            labels: data.topTreatments.map((t) => t.name),
            datasets: [
              {
                label: "Revenue",
                data: data.topTreatments.map((t) => t.amount),
                backgroundColor: [
                  TEAL,
                  TEAL2,
                  GOLD,
                  NAVY,
                  "#14a0a5",
                  "#c9a845",
                ],
                borderRadius: 6,
                borderSkipped: false,
              },
            ],
          },
          options: {
            indexAxis: "y",
            responsive: true,
            maintainAspectRatio: false,
            plugins: { legend: { display: false } },
            scales: {
              x: {
                grid: { color: gc },
                ticks: {
                  color: tc,
                  font: { size: 10 },
                  callback: (v) => "₹" + v.toLocaleString("en-IN"),
                },
                beginAtZero: true,
              },
              y: {
                grid: { display: false },
                ticks: { color: tc, font: { size: 11 } },
              },
            },
          },
        });
        if (treatRef.current.parentElement) {
          treatRef.current.parentElement.style.height =
            Math.max(80, data.topTreatments.length * 44 + 40) + "px";
        }
      }

      // ── 4. Payment method donut chart ─────────────────────
      if (methodChart.current) methodChart.current.destroy();
      if (
        methodRef.current &&
        data.paymentMethodStats &&
        data.paymentMethodStats.length > 0
      ) {
        const methods = data.paymentMethodStats;
        const colors = methods.map((m) => METHOD_COLORS[m.method] || "#94a3b8");
        methodChart.current = new window.Chart(methodRef.current, {
          type: "doughnut",
          data: {
            labels: methods.map((m) => m.method),
            datasets: [
              {
                data: methods.map((m) => m.amount || 0.001),
                backgroundColor: colors,
                borderWidth: 0,
                hoverOffset: 8,
              },
            ],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            cutout: "65%",
            plugins: { legend: { display: false } },
          },
        });
      }

      // ── 5. New vs Returning patients bar chart ────────────
      if (patientChart.current) patientChart.current.destroy();
      if (patientRef.current) {
        patientChart.current = new window.Chart(patientRef.current, {
          type: "bar",
          data: {
            labels: ["Patients"],
            datasets: [
              {
                label: "New",
                data: [data.newPatients || 0],
                backgroundColor: TEAL,
                borderRadius: 6,
                borderSkipped: false,
              },
              {
                label: "Returning",
                data: [data.returningPatients || 0],
                backgroundColor: GOLD,
                borderRadius: 6,
                borderSkipped: false,
              },
            ],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: { legend: { display: false } },
            scales: {
              x: {
                grid: { display: false },
                ticks: { color: tc, font: { size: 11 } },
              },
              y: {
                grid: { color: gc },
                ticks: { color: tc, font: { size: 10 } },
                beginAtZero: true,
                stepSize: 1,
              },
            },
          },
        });
      }
    });

    return () => {
      [trendChart, donutChart, treatChart, methodChart, patientChart].forEach(
        (c) => {
          if (c.current) c.current.destroy();
        },
      );
    };
  }, [data]);

  const deltaColor = (txt) => {
    if (!txt) return "#94a3b8";
    if (txt.startsWith("↑")) return "#16a34a";
    if (txt.startsWith("↓")) return "#dc2626";
    return "#d97706";
  };

  return (
    <div className="bd-wrap">
      <style>{css}</style>

      {/* ── Header ─────────────────────────────────────────── */}
      <div className="bd-header">
        <div>
          <div className="bd-header-title">📊 Hospital Dashboard</div>
          <div className="bd-header-sub">
            {data ? `Showing: ${period} · ${date}` : "Loading..."}
          </div>
        </div>
        <div className="bd-filters">
          <div className="bd-seg">
            {["daily", "weekly", "monthly", "yearly"].map((p) => (
              <button
                key={p}
                className={`bd-seg-btn${period === p ? " active" : ""}`}
                onClick={() => setPeriod(p)}
              >
                {p.charAt(0).toUpperCase() + p.slice(1)}
              </button>
            ))}
          </div>
          <input
            type="date"
            className="bd-date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
          <button className="bd-refresh-btn" onClick={fetchStats}>
            ↻ Refresh
          </button>
        </div>
      </div>

      <div className="bd-body">
        {loading ? (
          <div className="bd-loading">⏳ Loading dashboard data...</div>
        ) : !data ? (
          <div className="bd-empty">
            No data available. Create some invoices first.
          </div>
        ) : (
          <>
            {/* ── Compare Row ──────────────────────────────── */}
            <div className="bd-cmp-row">
              <div className="bd-cmp">
                <div className="bd-cmp-label">This period</div>
                <div className="bd-cmp-val">{fmt(data.curRevenue)}</div>
                <div className="bd-cmp-delta" style={{ color: TEAL }}>
                  ↑ Current period revenue
                </div>
              </div>
              <div className="bd-cmp">
                <div className="bd-cmp-label">Previous period</div>
                <div className="bd-cmp-val">{fmt(data.prevRevenue)}</div>
                <div
                  className="bd-cmp-delta"
                  style={{ color: deltaColor(data.deltaText) }}
                >
                  {data.deltaText}
                </div>
              </div>
              <div className="bd-cmp">
                <div className="bd-cmp-label">Collection rate</div>
                <div className="bd-cmp-val">{data.collectionRate}%</div>
                <div
                  className="bd-cmp-delta"
                  style={{
                    color: data.collectionRate >= 90 ? GREEN : "#d97706",
                  }}
                >
                  {data.collectionRate >= 100
                    ? "↑ Full collection"
                    : data.collectionRate >= 80
                      ? "↑ Good collection"
                      : "⚠ Dues pending"}
                </div>
              </div>
            </div>

            {/* ── KPI Grid ─────────────────────────────────── */}
            <div className="bd-kpi-grid">
              <div className="bd-kpi">
                <div className="bd-kpi-icon">💰</div>
                <div className="bd-kpi-val">{fmt(data.curRevenue)}</div>
                <div className="bd-kpi-label">{data.chartSub || "Revenue"}</div>
                <div className="bd-kpi-change" style={{ color: TEAL }}>
                  ↑ {data.paidCount} paid invoice
                  {data.paidCount !== 1 ? "s" : ""}
                </div>
              </div>
              <div className="bd-kpi">
                <div className="bd-kpi-icon">✅</div>
                <div className="bd-kpi-val">{data.paidCount}</div>
                <div className="bd-kpi-label">Paid invoices</div>
                <div className="bd-kpi-change" style={{ color: GREEN }}>
                  {data.paidCount + data.unpaidCount + data.partialCount > 0
                    ? `↑ ${Math.round((data.paidCount / (data.paidCount + data.unpaidCount + data.partialCount)) * 100)}% of total`
                    : "→ No invoices"}
                </div>
              </div>
              <div className="bd-kpi">
                <div className="bd-kpi-icon">⚠️</div>
                <div className="bd-kpi-val">
                  {data.unpaidCount + data.partialCount}
                </div>
                <div className="bd-kpi-label">Pending invoices</div>
                <div
                  className="bd-kpi-change"
                  style={{
                    color:
                      data.unpaidCount + data.partialCount > 0
                        ? "#dc2626"
                        : GREEN,
                  }}
                >
                  {data.unpaidCount + data.partialCount > 0
                    ? "↓ Action needed"
                    : "→ All cleared"}
                </div>
              </div>
              <div className="bd-kpi">
                <div className="bd-kpi-icon">📋</div>
                <div className="bd-kpi-val">{fmt(data.totalDue)}</div>
                <div className="bd-kpi-label">Outstanding dues</div>
                <div
                  className="bd-kpi-change"
                  style={{ color: data.totalDue > 0 ? "#d97706" : GREEN }}
                >
                  {data.totalDue > 0 ? "⚠ Dues pending" : "↑ No dues"}
                </div>
              </div>
            </div>

            {/* ── Revenue Chart + Payment Status Donut ─────── */}
            <div className="bd-charts-row">
              <div className="bd-card">
                <div className="bd-card-header">
                  <div>
                    <div className="bd-card-title">{data.chartTitle}</div>
                    <div className="bd-card-sub">{data.chartSub}</div>
                  </div>
                </div>
                <div className="bd-legend">
                  <span>
                    <span
                      className="bd-dot"
                      style={{ background: TEAL }}
                    ></span>
                    Revenue
                  </span>
                  <span>
                    <span
                      className="bd-dot"
                      style={{ background: GOLD }}
                    ></span>
                    Target
                  </span>
                </div>
                <div style={{ position: "relative", height: 200 }}>
                  <canvas ref={trendRef} />
                </div>
              </div>
              <div className="bd-card">
                <div className="bd-card-header">
                  <div>
                    <div className="bd-card-title">Payment breakdown</div>
                    <div className="bd-card-sub">{data.chartSub}</div>
                  </div>
                </div>
                <div className="bd-legend">
                  <span>
                    <span
                      className="bd-dot"
                      style={{ background: TEAL }}
                    ></span>
                    Paid
                  </span>
                  <span>
                    <span
                      className="bd-dot"
                      style={{ background: GOLD }}
                    ></span>
                    Partial
                  </span>
                  <span>
                    <span className="bd-dot" style={{ background: RED }}></span>
                    Unpaid
                  </span>
                </div>
                <div style={{ position: "relative", height: 200 }}>
                  <canvas ref={donutRef} />
                </div>
              </div>
            </div>

            {/* ── Payment Method + New vs Returning Patients ── */}
            <div className="bd-charts-row-equal">
              {/* Payment Method Donut */}
              <div className="bd-card">
                <div className="bd-card-header">
                  <div>
                    <div className="bd-card-title">💳 Payment methods</div>
                    <div className="bd-card-sub">{data.chartSub}</div>
                  </div>
                </div>
                {data.paymentMethodStats &&
                data.paymentMethodStats.length > 0 ? (
                  <>
                    <div style={{ position: "relative", height: 140 }}>
                      <canvas ref={methodRef} />
                    </div>
                    <div className="bd-method-list">
                      {data.paymentMethodStats.map((m) => (
                        <div key={m.method} className="bd-method-row">
                          <div className="bd-method-name">
                            <span
                              className="bd-method-dot"
                              style={{
                                background:
                                  METHOD_COLORS[m.method] || "#94a3b8",
                              }}
                            ></span>
                            {m.method}
                          </div>
                          <div className="bd-method-amount">
                            {fmt(m.amount)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="bd-empty" style={{ padding: 20 }}>
                    No payment data
                  </div>
                )}
              </div>

              {/* New vs Returning Patients */}
              <div className="bd-card">
                <div className="bd-card-header">
                  <div>
                    <div className="bd-card-title">👥 Patient visits</div>
                    <div className="bd-card-sub">{data.chartSub}</div>
                  </div>
                </div>
                <div className="bd-legend">
                  <span>
                    <span
                      className="bd-dot"
                      style={{ background: TEAL }}
                    ></span>
                    New
                  </span>
                  <span>
                    <span
                      className="bd-dot"
                      style={{ background: GOLD }}
                    ></span>
                    Returning
                  </span>
                </div>
                <div style={{ position: "relative", height: 140 }}>
                  <canvas ref={patientRef} />
                </div>
                <div style={{ display: "flex", gap: 12, marginTop: 12 }}>
                  <div
                    style={{
                      flex: 1,
                      background: "rgba(13,115,119,0.08)",
                      borderRadius: 8,
                      padding: "10px 14px",
                      textAlign: "center",
                    }}
                  >
                    <div style={{ fontSize: 22, fontWeight: 700, color: TEAL }}>
                      {data.newPatients || 0}
                    </div>
                    <div style={{ fontSize: 11, color: "#94a3b8" }}>
                      New patients
                    </div>
                  </div>
                  <div
                    style={{
                      flex: 1,
                      background: "rgba(201,168,69,0.08)",
                      borderRadius: 8,
                      padding: "10px 14px",
                      textAlign: "center",
                    }}
                  >
                    <div style={{ fontSize: 22, fontWeight: 700, color: GOLD }}>
                      {data.returningPatients || 0}
                    </div>
                    <div style={{ fontSize: 11, color: "#94a3b8" }}>
                      Returning
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* ── Top Treatments Bar Chart ──────────────────── */}
            {data.topTreatments && data.topTreatments.length > 0 && (
              <div className="bd-card" style={{ marginBottom: 16 }}>
                <div className="bd-card-header">
                  <div className="bd-card-title">
                    🏥 Top treatments — revenue breakdown
                  </div>
                  <div className="bd-card-sub">{data.chartSub}</div>
                </div>
                <div style={{ position: "relative", height: 100 }}>
                  <canvas ref={treatRef} />
                </div>
              </div>
            )}

            {/* ── Summary Stats Row ─────────────────────────── */}
            <div className="bd-summary-row">
              <div className="bd-summary">
                <div className="bd-summary-icon">📊</div>
                <div className="bd-summary-val">
                  {data.totalInvoiceCount || 0}
                </div>
                <div className="bd-summary-label">
                  Total invoices this period
                </div>
              </div>
              <div className="bd-summary">
                <div className="bd-summary-icon">📈</div>
                <div className="bd-summary-val">
                  {fmt(data.avgInvoice || 0)}
                </div>
                <div className="bd-summary-label">Average invoice value</div>
              </div>
              <div className="bd-summary">
                <div className="bd-summary-icon">🏆</div>
                <div className="bd-summary-val">
                  {fmt(data.maxInvoice || 0)}
                </div>
                <div className="bd-summary-label">Highest invoice value</div>
              </div>
            </div>

            {/* ── Invoice List ──────────────────────────────── */}
            <div className="bd-card">
              <div className="bd-card-header">
                <div className="bd-card-title">Invoices this period</div>
                <div className="bd-card-sub">
                  {(data.recentInvoices || []).length} invoice
                  {(data.recentInvoices || []).length !== 1 ? "s" : ""}
                </div>
              </div>
              {!data.recentInvoices || data.recentInvoices.length === 0 ? (
                <div className="bd-empty">No invoices for this period.</div>
              ) : (
                data.recentInvoices.map((inv) => (
                  <div key={inv._id} className="bd-inv-row">
                    <div>
                      <div className="bd-inv-name">{inv.patientName}</div>
                      <div className="bd-inv-meta">
                        {inv.invoiceNumber} · {inv.invoiceDate} ·{" "}
                        {inv.treatment}
                      </div>
                    </div>
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 10 }}
                    >
                      <span
                        className={`bd-badge ${inv.paymentStatus === "paid" ? "bd-badge-paid" : inv.paymentStatus === "partial" ? "bd-badge-partial" : "bd-badge-unpaid"}`}
                      >
                        {inv.paymentStatus === "paid"
                          ? "Paid"
                          : inv.paymentStatus === "partial"
                            ? "Partial"
                            : "Unpaid"}
                      </span>
                      <div
                        style={{
                          fontSize: 13,
                          fontWeight: 700,
                          color: "#0a1628",
                          minWidth: 70,
                          textAlign: "right",
                        }}
                      >
                        {fmt(inv.totalAmount)}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
